import { validateVfx, type VfxDefinition } from "./definition.ts";
import { DRINK_BURST } from "./definitions/drink-burst.ts";
import { LEVEL_UP } from "./definitions/level-up.ts";
import { XP_BAR_SURGE } from "./definitions/xp-bar-surge.ts";

/**
 * The vfx the page knows (ADR-134 decision 3): every definition, validated once as the registry is made, looked up by
 * its id. A new vfx is a file under `definitions/` and a line here.
 */
export class VfxRegistry {
  private readonly byId = new Map<string, number>();

  /**
   * @param definitions The definitions, each with an id of its own.
   * @throws Error when a definition is invalid or two share an id.
   */
  public constructor(public readonly definitions: readonly VfxDefinition[]) {
    definitions.forEach((definition, index) => {
      const problems = validateVfx(definition);
      if (problems.length > 0) {
        throw new Error(`vfx ${definition.id} is invalid: ${problems.join("; ")}`);
      }
      if (this.byId.has(definition.id)) {
        throw new Error(`two vfx share the id ${definition.id}`);
      }
      this.byId.set(definition.id, index);
    });
  }

  /**
   * The place of a definition in the registry, the number the feed carries.
   *
   * @param id The vfx's id.
   * @returns The index, or -1 for an id the registry lacks.
   */
  public indexOf(id: string): number {
    return this.byId.get(id) ?? -1;
  }

  /**
   * A definition by its place.
   *
   * @param index The index from `indexOf`.
   * @returns The definition, or undefined past the end.
   */
  public at(index: number): VfxDefinition | undefined {
    return this.definitions[index];
  }
}

/**
 * The page's registry.
 */
export const VFX_REGISTRY = new VfxRegistry([LEVEL_UP, DRINK_BURST, XP_BAR_SURGE]);
